import { Badge, Box, Grid, Paper, Tab, Tabs } from '@mui/material';
import PropTypes from 'prop-types';
import { filter, map, some } from 'lodash';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import useTabContext, { TabProvider } from 'common/hooks/useTabContext';
import useActionItemsCount from 'common/hooks/useActionItemsCount';
import useRoles from 'common/hooks/useRoles';
import { recordAuditEvent } from 'store/thunks/patientThunks';
import PatientCommentsTab from './CommentsTab/PatientCommentsTab';
import PatientPrescriptionsTab from './PrescriptionsTab/PatientPrescriptionsTab';
import PatientActionItemsTab from './PatientActionItemsTab';
import PatientShippingTab from './ShippingTab/PatientShippingTab';
import ProvidersTab from './ProvidersTab';
import InsurancesTab from './InsurancesTab';
import OrderHistoryTab from './OrderHistoryTab/OrderHistory';
import MedicalHistoryTab from './MedicalHistoryTab/index';
import PatientRelayOrdersTab from './RelayOrdersTab/PatientRelayOrdersTab';
import PatientContactDetailsTab, {
  CONTACT_DETAILS_TAB_VALUE,
} from './ContactDetailsTab/PatientContactDetailsTab';

const ActionItemCountBadge = () => {
  const { mpi } = useParams();
  const count = useActionItemsCount({ mpi });

  return count ? <Badge color='error' badgeContent={count} sx={{ mx: 2 }} /> : null;
};

const TabPanel = ({ children, value }) => {
  const { tab } = useTabContext();
  const hidden = value !== tab;

  return (
    <div
      role='tabpanel'
      hidden={hidden}
      id={`patient-details-tabpanel-${value}`}
      aria-labelledby={`patient-details-tab-${value}`}
    >
      <Box sx={{ py: 3, px: 1 }}>{children}</Box>
    </div>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.string.isRequired,
};

// Keyed by tabValue, the same key TabPanel builds its id and aria-labelledby from, so each tab and
// its panel point at each other. (Keyed by index they never matched, and a role-gated tab would
// shift every index after it.)
const a11yProps = (tabValue) => ({
  id: `patient-details-tab-${tabValue}`,
  'aria-controls': `patient-details-tabpanel-${tabValue}`,
});

const tabsDetails = [
  {
    tabName: (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        Action Items
        <ActionItemCountBadge />
      </Box>
    ),
    Component: PatientActionItemsTab,
    tabValue: 'PatientActionItemsTab',
  },
  {
    tabName: 'Order History',
    Component: OrderHistoryTab,
    tabValue: 'OrderHistoryTab',
  },
  {
    tabName: 'Relayed Orders',
    Component: PatientRelayOrdersTab,
    tabValue: 'PatientRelayOrdersTab',
    // POM-6279: relay orders are POM sys-admin only, matching core's isPOMAdmin gate.
    requiredRole: 'viewRelayedOrders',
  },
  {
    tabName: 'Medical History',
    Component: MedicalHistoryTab,
    tabValue: 'PatientMedicalHistoryTab',
  },
  {
    tabName: 'Providers',
    Component: ProvidersTab,
    tabValue: 'PatientProvidersTab',
  },
  {
    tabName: 'Insurances',
    Component: InsurancesTab,
    tabValue: 'PatientInsuranceTab',
  },
  {
    tabName: 'Prescriptions',
    Component: PatientPrescriptionsTab,
    tabValue: 'PatientPrescriptionsTab',
  },
  {
    tabName: 'Shipping',
    Component: PatientShippingTab,
    tabValue: 'PatientShippingTab',
  },
  {
    tabName: 'Contact Details',
    Component: PatientContactDetailsTab,
    tabValue: CONTACT_DETAILS_TAB_VALUE,
  },
  {
    tabName: 'Comments',
    Component: PatientCommentsTab,
    tabValue: 'PatientCommentsTab',
  },
];

const tabToEventTypeMap = {
  PatientActionItemsTab: 'VIEW_ACTION_ITEMS',
  OrderHistoryTab: 'VIEW_ORDER_HISTORY',
  PatientMedicalHistoryTab: 'VIEW_MEDICAL_HISTORY',
  PatientPrescriptionsTab: 'VIEW_PRESCRIPTION_DETAILS',
  PatientShippingTab: 'VIEW_SHIPPING_HISTORY',
  PatientRelayOrdersTab: 'VIEW_RELAY_ORDERS',
};

/**
 * The tabs this user may see. Applied to the tab strip AND the panels, so a role-gated tab's panel
 * is never mounted -- and nothing in it ever fetches -- for anyone without the role.
 */
const useVisibleTabsDetails = () => {
  const userRoles = useRoles();

  return useMemo(
    () => filter(tabsDetails, ({ requiredRole }) => !requiredRole || userRoles[requiredRole]),
    [userRoles]
  );
};

const AuditEventTracker = () => {
  const dispatch = useDispatch();
  const { mpi } = useParams();
  const { tab } = useTabContext();

  useEffect(() => {
    const eventType = tabToEventTypeMap[tab];
    if (eventType && mpi) {
      dispatch(recordAuditEvent({ eventType, patientId: mpi }));
    }
  }, [tab, mpi, dispatch]);

  return null;
};

const TabList = () => {
  const { tab, setTab } = useTabContext();
  const visibleTabsDetails = useVisibleTabsDetails();
  const firstVisibleTab = visibleTabsDetails[0]?.tabValue;
  const isTabVisible = some(visibleTabsDetails, { tabValue: tab });

  // The selected tab can stop being visible while the page stays mounted -- a role or tenant
  // change takes a gated tab away. Fall back to the first visible tab rather than leave every
  // panel hidden under a tab that no longer exists.
  useEffect(() => {
    if (!isTabVisible && firstVisibleTab) {
      setTab(null, firstVisibleTab);
    }
  }, [isTabVisible, firstVisibleTab, setTab]);

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Tabs
        value={isTabVisible ? tab : firstVisibleTab}
        onChange={setTab}
        aria-label='Patient Details Tabs'
        scrollButtons='auto'
        sx={{ maxWidth: '85vw' }}
        variant='scrollable'
      >
        {map(visibleTabsDetails, ({ tabName, tabValue }) => (
          <Tab label={tabName} {...a11yProps(tabValue)} key={tabValue} value={tabValue} />
        ))}
      </Tabs>
    </Box>
  );
};

const PatientDetailsTabs = () => {
  const visibleTabsDetails = useVisibleTabsDetails();

  return (
    <TabProvider initialValue='PatientActionItemsTab'>
      <AuditEventTracker />
      <Grid container component={Paper} sx={{ px: 2, minHeight: '90vh' }} direction='column'>
        <TabList />

        {map(visibleTabsDetails, ({ Component, tabValue }) => (
          <TabPanel value={tabValue} key={tabValue}>
            <Component />
          </TabPanel>
        ))}
      </Grid>
    </TabProvider>
  );
};

export default PatientDetailsTabs;
